/**
 * RecentlyPlayed — horizontal carousel of the last 6 games the player launched.
 *
 * Data comes from GET /recently-played (svc-game), backed by game.game_launches.
 * Hidden entirely when the player has no launch history yet.
 */
import { useTranslation } from 'react-i18next';
import { useRecentGames, type RecentGameInfo } from '../../features/games/useRecentGames.js';
import { useGameLaunch } from '../../features/games/useGameLaunch.js';
import { GameCard, type GameLike } from './GameCard.js';

interface RecentlyPlayedProps {
  className?: string;
}

function recentToGameLike(r: RecentGameInfo): GameLike {
  return {
    id: r.gameId,
    name: r.gameName,
    code: r.gameCode,
    gameType: 'other',
    providerId: r.providerId,
    providerCode: r.providerCode,
    providerDisplayName: r.providerDisplayName,
  };
}

function CardSkeleton() {
  return (
    <div className="shrink-0 w-44 flex flex-col gap-2">
      <div className="aspect-[8/5] rounded-xl bg-[var(--bg-elevated)]/50 animate-pulse" />
      <div className="h-3 w-20 rounded bg-[var(--bg-elevated)]/50 animate-pulse" />
      <div className="h-7 rounded-md bg-[var(--bg-elevated)]/50 animate-pulse" />
    </div>
  );
}

export function RecentlyPlayed({ className }: RecentlyPlayedProps) {
  const { t, i18n } = useTranslation(['lobby', 'games', 'common']);
  const { data: recentGames, isPending } = useRecentGames(6);
  const { launch, modal: launchModal } = useGameLaunch({ lang: i18n.language ?? 'th' });

  // Hide the section if we have no data and are done loading
  if (!isPending && (!recentGames || recentGames.length === 0)) {
    return null;
  }

  function onPlay(providerId: string, gameId: string, gameName: string) {
    // No mode hint — recent-games payload doesn't carry launchMode yet, so
    // we let the dispatcher fall back to the post-fetch open path. Players
    // may hit a popup-blocked toast on their first click of the session for
    // popup-mode providers.
    void launch({
      id: gameId,
      providerId,
      name: gameName,
      launchMode: undefined,
      launchMethod: undefined,
    });
  }

  return (
    <section className={className}>
      {launchModal}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[15px] font-bold text-[var(--text-primary)]">
          {t('lobby:recentlyPlayed')}
        </h2>
      </div>

      <div
        className="flex gap-3 overflow-x-auto pb-1 scrollbar-none"
        style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
      >
        {isPending
          ? Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)
          : (recentGames ?? []).map((game) => {
              const asLike: GameLike = recentToGameLike(game);
              return (
                <div key={game.gameId} className="shrink-0 w-44">
                  <GameCard
                    game={asLike}
                    onPlay={() => onPlay(game.providerId, game.gameId, game.gameName)}
                  />
                </div>
              );
            })}
      </div>
    </section>
  );
}
